//* Import react and typings
import React from "react";

//* Import react-bootstrap components
import { Table, Button, Card } from "react-bootstrap";

//* Import icons from lucide-react
import { Trash2, Users, FileCheck, Search } from "lucide-react";

//* Import CSVStudent type
import { CSVStudent } from "../../../../hooks/CSV/students/useCSVStudentData";

type CSVStudentPreviewTableProps = {
  data: CSVStudent[];
  searchTerm: string;
  handleRemovePreviewItem: (index: number) => void;
};

export const CSVStudentPreviewTable: React.FC<CSVStudentPreviewTableProps> = ({
  data,
  searchTerm,
  handleRemovePreviewItem,
}) => {
  const filteredData = data.filter(
    (student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const EmptyState = () => (
    <tr>
      <td colSpan={3}>
        <div className="text-center py-4">
          <FileCheck size={32} className="text-muted mb-2 opacity-50" />
          <p className="text-muted mb-0 fw-medium">
            Nenhum aluno válido para importar
          </p>
          {searchTerm && (
            <p className="text-muted small mb-0">
              Tente ajustar seus critérios de busca
            </p>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <Card className="border-0 shadow-sm mt-4">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FileCheck size={20} className="text-success me-2" />
            <div>
              <h6 className="mb-0 fw-semibold">Prévia dos Dados Válidos</h6>
              <p className="text-muted small mb-0">
                alunos prontos para importação
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            {searchTerm && (
              <div className="d-flex align-items-center text-primary small">
                <Search size={14} className="me-1" />
                Filtrando resultados
              </div>
            )}
            <span className="badge bg-success bg-opacity-10 text-success px-2 py-1 rounded-pill d-flex align-items-center">
              <Users size={14} className="me-1" />
              {filteredData.length} aluno
              {filteredData.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </Card.Header>

      <div
        className="table-responsive"
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          scrollbarWidth: "thin",
        }}
      >
        <Table hover className="mb-0">
          <thead className="bg-light">
            <tr>
              <th
                className="px-3 py-3 border-bottom text-muted"
                style={{ width: "45%" }}
              >
                Nome
              </th>
              <th
                className="px-3 py-3 border-bottom text-muted"
                style={{ width: "45%" }}
              >
                E-mail
              </th>
              <th
                className="px-3 py-3 border-bottom text-center text-muted"
                style={{ width: "10%" }}
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <EmptyState />
            ) : (
              filteredData.map((item, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-light bg-opacity-50" : ""}
                >
                  <td className="px-3 py-2">
                    <div className="d-flex align-items-center">
                      <Users size={16} className="text-muted me-2 opacity-50" />
                      <span className="fw-medium text-truncate">
                        {item.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-muted small">{item.email}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Button
                      variant="link"
                      className="text-danger p-1"
                      onClick={() => handleRemovePreviewItem(index)}
                      title="Remover aluno"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </Card>
  );
};

export default CSVStudentPreviewTable;
